import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import dateFormat from 'date-fns/format';
import { get as _get, has as _has } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { filter, first, take } from 'rxjs/operators';

// Store
import {
  ScheduledPaymentActions,
  ScheduledPaymentSelectors
} from '@amfam/billing/schedulepayment/data-access';
// date-fns
import { AnalyticsActions } from '@amfam/shared/analytics';
import {
  BillAccountPaymentsScheduleRow,
  BillingAnalytics,
  PaymentStatus,
  SchedulePaymentPayloadModel
} from '@amfam/shared/models';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { routerQuery } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  UtilService
} from '@amfam/shared/utility/shared-services';

@Component({
  selector: 'ds-bill-account-payments-scheduled-delete',
  templateUrl: './bill-account-payments-scheduled-delete.component.html',
  styleUrls: ['./bill-account-payments-scheduled-delete.component.scss'],
  standalone: false
})
export class BillAccountScheduledPaymentDeleteComponent implements OnInit, OnDestroy {
  @Input() selectedScheduledPayment: BillAccountPaymentsScheduleRow;

  @Output() deleteSubmittedEvent = new EventEmitter();
  @Output() deleteSuccessEvent = new EventEmitter();
  @Output() deleteErrorEvent = new EventEmitter();
  @Output() closeEvent = new EventEmitter();
  @Output() setCancelPathEvent = new EventEmitter<string>();

  allowDelete = true;

  loading: Observable<boolean>;

  public isAdminApp = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    public roleService: ImpersonateRolesService,
    private store: Store,
    private utilService: UtilService,
    private applicationService: ApplicationService
  ) {}

  ngOnInit() {
    if (this.isAdminApp) {
      this.store.dispatch(
        AnalyticsActions.sendAnalytic({
          options: {
            page: BillingAnalytics.pageDeleteSchedulePaymentStart
          }
        })
      );
    }
    if (
      _has(this.selectedScheduledPayment, 'paymentDetails.paymentStatus') &&
      this.selectedScheduledPayment.paymentDetails.paymentStatus === PaymentStatus.AUTHORIZED
    ) {
      this.allowDelete = false;
      return;
    }

    // AS: Set the cancel path, so that we know where to take the user if they click on cancel.
    this.store
      .select(routerQuery.getRouterPreviousStateUrl)
      .pipe(take(1))
      .subscribe((url: string) => this.setCancelPathEvent.emit(url));
  }

  confirmDelete() {
    this.loading = this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsAnyLoading);
    const inputDate = dateFormat(
      this.selectedScheduledPayment.paymentDetails.receivedDate,
      'YYYY-MM-DD'
    );
    // generate coorelation id
    const correlationId = this.utilService.generateId();
    const paymentInfoObj: SchedulePaymentPayloadModel = {
      payment: {
        receivedDate: inputDate,
        totalPaymentAmount: Number(
          _get(this.selectedScheduledPayment, 'paymentDetails.totalPaymentAmount', 0)
        ).toFixed(2),
        paymentAccount: _get(this.selectedScheduledPayment, 'paymentDetails.paymentAccount'),
        billAccounts: _get(this.selectedScheduledPayment, 'paymentDetails.billAccounts')
      },
      paymentId: this.selectedScheduledPayment.paymentDetails.paymentId,
      correlationId: correlationId
    };
    this.deleteSubmittedEvent.emit();
    this.store.dispatch(ScheduledPaymentActions.scheduledPaymentsDelete(paymentInfoObj));
    this.store
      .select(ScheduledPaymentSelectors.selectScheduledPaymentsFilteredNotifications(correlationId))
      .pipe(
        filter(scheduledPayments => scheduledPayments && scheduledPayments.length === 1),
        first()
      )
      .subscribe(scheduledPayments => {
        // If we do not have any error emit to the calling parent component
        if (!_get(scheduledPayments[0], 'error')) {
          this.deleteSuccessEvent.emit();
        } else {
          this.deleteErrorEvent.emit(_get(scheduledPayments[0], 'error'));
        }
      });
  }

  close() {
    this.closeEvent.emit();
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
